import { ConversationContext } from '@justrelate/llm-conversation';
import { DataClass } from 'scrivito';

import { Language } from './LanguageContext';
import {
  ToolDefinition,
  WebsiteConfig,
  buildDataModelDescription,
  defineCRUDTools,
  defineSiteSearchTool,
  resolveTool,
} from './tool_integration';

type InitialMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export interface LlmConfig {
  systemPrompt: string;

  dataClasses?: DataClass[];
  dataClassToolVersion?: 'v1' | 'v2';
  initialMessages?: InitialMessage[];
  instanceId?: string;
  model?: string;
  sites?: Record<string, WebsiteConfig>;
  systemNotice?: () => Promise<string | null>;
  temperature?: number;
  tools?: ToolDefinition<unknown>[];
  useAnonymously?: boolean;
}

// TODO needs to be more conditional, i.e. only include data model formatting, if a data model is present
const formatPrompt = `
    You can use Markdown to format your messages, including tables. Keep tables narrow (few columns, short cell content) since they are displayed in a narrow chat window.
    When you suggest a prompt, wrap it in a link using the anchor "run-prompt" like this: [the text of the prompt](#run-prompt). When suggesting multiple prompts, put each on its own line. Do not separate them with pipe characters.
    When referring to content with a _url attribute, always link using this URL: So if a content item has the title 'My Page' and the _url is 'https://example.com/my_page', you can link to it like this: [My Page](https://example.com/my_page).
  When showing or listing a data item of a certain data type (e.g. an order) and there is an _id attribute, always link to each item like this: [text](/_data-item/<the data type>/<the _id>), for example [this order](/_data-item/Order/1234).
  `;

export async function buildConversationContext(
  config: LlmConfig,
  language: Language,
): Promise<ConversationContext> {
  return {
    model: config.model,
    temperature: config.temperature,
    systemPrompt: await buildSystemPrompt(config, language),
    tools: buildTools(config),
  };
}

async function buildSystemPrompt(
  config: LlmConfig,
  language: Language,
): Promise<string> {
  const dataModelPrompt =
    config.dataClasses &&
    `This is the data model:
      ${await buildDataModelDescription(config.dataClasses)}`;

  const languagePrompt = `You respond in ${getLanguageDisplayName(language)} unless instructed otherwise.`;

  return `${config.systemPrompt}

      ${languagePrompt}

      ${dataModelPrompt}

      ${formatPrompt}`;
}

function getLanguageDisplayName(language: Language): string {
  return (
    new Intl.DisplayNames(['en'], { type: 'language' }).of(language) ||
    'English'
  );
}

export function buildTools({
  sites,
  dataClasses,
  dataClassToolVersion,
  tools = [],
}: LlmConfig) {
  return [
    ...tools.map(resolveTool),
    ...((dataClasses &&
      defineCRUDTools(dataClassToolVersion ?? 'v1', dataClasses)) ??
      []),
    ...((sites && [defineSiteSearchTool(sites)]) ?? []),
  ];
}
